/**
 * History Store
 * Zustand store with localStorage persistence for invoice history
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Invoice } from '@shared/schemas/invoice.schema';
import { logger } from '@/utils/logger';

interface HistoryItem {
  invoice: Invoice;
  savedAt: string;
}

interface HistoryStore {
  // State
  history: HistoryItem[];
  isOpen: boolean;

  // Actions
  addToHistory: (invoice: Invoice) => void;
  updateInHistory: (invoice: Invoice) => void;
  removeFromHistory: (invoiceId: string) => void;
  clearHistory: () => void;
  loadFromHistory: () => void;
  setOpen: (isOpen: boolean) => void;
}

const MAX_HISTORY_ITEMS = 50;

export const useHistoryStore = create<HistoryStore>()(
  persist(
    (set, get) => ({
      // Initial state
      history: [],
      isOpen: false,

      // Actions
      addToHistory: (invoice) => {
        const { history } = get();
        logger.log('[HistoryStore] Adding invoice to history:', {
          invoiceId: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
          currentHistoryCount: history.length,
        });

        // Check if invoice already exists in history (by ID only, not invoiceNumber)
        const existingIndex = history.findIndex(
          (item) => item.invoice.id === invoice.id
        );

        let newHistory: HistoryItem[];

        if (existingIndex !== -1) {
          // Remove existing entry if found (updating existing invoice)
          logger.log('[HistoryStore] Invoice already exists, updating entry');
          newHistory = history.filter((_, index) => index !== existingIndex);
        } else {
          newHistory = [...history];
        }

        // Add new entry at the beginning
        newHistory.unshift({
          invoice,
          savedAt: new Date().toISOString(),
        });

        // Limit history size
        if (newHistory.length > MAX_HISTORY_ITEMS) {
          newHistory = newHistory.slice(0, MAX_HISTORY_ITEMS);
        }

        logger.log('[HistoryStore] Invoice added. New history count:', newHistory.length);
        set({ history: newHistory });
      },

      updateInHistory: (invoice) => {
        const { history } = get();
        logger.log('[HistoryStore] Updating invoice in history:', {
          invoiceId: invoice.id,
          invoiceNumber: invoice.invoiceNumber,
        });

        // Find the existing invoice in history
        const existingIndex = history.findIndex(
          (item) => item.invoice.id === invoice.id
        );

        if (existingIndex !== -1) {
          // Update the existing invoice
          const newHistory = [...history];
          newHistory[existingIndex] = {
            invoice,
            savedAt: new Date().toISOString(),
          };
          logger.log('[HistoryStore] Invoice updated in history');
          set({ history: newHistory });
        } else {
          logger.log('[HistoryStore] Invoice not found in history, adding instead');
          // If not found, add it as a new entry
          get().addToHistory(invoice);
        }
      },

      removeFromHistory: (invoiceId) => {
        const { history } = get();
        set({
          history: history.filter(
            (item) => item.invoice.id !== invoiceId
          ),
        });
      },

      clearHistory: () => {
        set({ history: [] });
      },

      loadFromHistory: () => {
        // This will be used to load an invoice into the main store
        // The actual loading will be handled by the component
        set({ isOpen: false });
      },

      setOpen: (isOpen) => {
        set({ isOpen });
      },
    }),
    {
      name: 'invoice-history-storage', // localStorage key
    }
  )
);
